import { ART_PAL, ball, body, fade, mix, n, shade, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Lucky Lever scene behind the game zone: a lucky-goods shop in the evening (a warm afternoon in light) — lacquered
 * shelves at both sides with gourds, jade jars and a plate of peaches, strings of red lanterns in the top corners,
 * warm wood floor. The centre stays calm (the machine sits there): only the wall and a soft pool of light. Motifs are
 * marked `data-motif="<x>"` (tests). No filters, no text: a static image, cheap to paint.
 */
export function luckyLeverScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sl${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const floor = wide ? 760 : 700;

  const wall = P.dark ? mix(P.bg, P.red, 0.16) : mix(P.gold, '#FFFFFF', 0.84);
  const wallLow = P.dark ? mix(P.bg, P.orange, 0.1) : mix(P.orange, '#FFFFFF', 0.86);
  const wood = shade(mix(P.orange, P.gold, 0.3), P.dark ? 0.6 : 0.42);
  const plank = shade(mix(P.orange, P.red, 0.3), P.dark ? 0.45 : 0.25);
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.6);
  const ink = P.dark ? '#FFFFFF' : shade(P.red, 0.4);
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  /** a shelf board with its two brackets */
  const shelf = (x0: number, x1: number, y: number) =>
    `<rect x="${x0}" y="${y}" width="${x1 - x0}" height="12" fill="${plank}"/>` +
    `<path d="M${x0 + 20} ${y + 12}l14 22M${x1 - 20} ${y + 12}l-14 22" stroke="${plank}" stroke-width="6"/>`;
  /** a bottle gourd standing on y */
  const gourd = (x: number, y: number, s: number) =>
    `<path data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})" d="M0 0C-16 0 -24 -10 -22 -20C-20 -30 -10 -32 -8 -38C-14 -42 -14 -56 0 -56C14 -56 14 -42 8 -38C10 -32 20 -30 22 -20C24 -10 16 0 0 0Z" fill="url(#${p}gr)"/>` +
    `<path d="M${n(x - 7 * s)} ${n(y - 37 * s)}h${n(14 * s)}" stroke="${P.red}" stroke-width="${n(3 * s)}"/>`;
  /** a jade jar with a gold lid */
  const jar = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})">` +
    `<path d="M-16 0Q-24 -22 -12 -40H12Q24 -22 16 0Z" fill="url(#${p}jd)"/>` +
    `<rect x="-13" y="-46" width="26" height="7" rx="3" fill="${gold}"/></g>`;
  /** three peaches on a plate */
  const peaches = (x: number, y: number, s: number) =>
    `<g data-motif="${Math.round(x)}" transform="translate(${x} ${y}) scale(${s})" fill="url(#${p}pc)">` +
    `<ellipse cx="0" cy="-3" rx="34" ry="6" fill="${tint(P.sky, P.dark ? 0.1 : 0.6)}" fill-opacity=".8"/>` +
    `<path d="M-14 -24C-16 -20 -26 -20 -26 -12C-26 -6 -20 -4 -14 -4C-8 -4 -2 -6 -2 -12C-2 -20 -12 -20 -14 -24Z"/>` +
    `<path d="M14 -24C12 -20 2 -20 2 -12C2 -6 8 -4 14 -4C20 -4 26 -6 26 -12C26 -20 16 -20 14 -24Z"/>` +
    `<path d="M0 -38C-2 -34 -12 -34 -12 -26C-12 -20 -6 -18 0 -18C6 -18 12 -20 12 -26C12 -34 2 -34 0 -38Z"/></g>`;
  /** a far lantern: glow + a red drum + gold caps */
  const lantern = (x: number, y: number, r: number) =>
    `<g data-motif="${Math.round(x)}"><circle cx="${n(x)}" cy="${n(y)}" r="${n(r * 3.6)}" fill="url(#${p}lh)"/>` +
    `<rect x="${n(x - r)}" y="${n(y - r * 1.2)}" width="${n(r * 2)}" height="${n(r * 2.4)}" rx="${n(r * 0.6)}" fill="url(#${p}ln)"/>` +
    `<rect x="${n(x - r * 0.6)}" y="${n(y - r * 1.5)}" width="${n(r * 1.2)}" height="${n(r * 0.35)}" fill="${gold}"/>` +
    `<rect x="${n(x - r * 0.6)}" y="${n(y + r * 1.15)}" width="${n(r * 1.2)}" height="${n(r * 0.35)}" fill="${gold}"/></g>`;
  /** a cord of lanterns sagging between x0 and x1 */
  const garland = (x0: number, x1: number, y0: number, sag: number, count: number, r: number) => {
    let s = `<path d="M${x0} ${y0}Q${(x0 + x1) / 2} ${y0 + sag * 2} ${x1} ${y0}" fill="none" stroke="${ink}" stroke-opacity="${o(0.18, 0.3)}" stroke-width="1.5"/>`;
    for (let i = 1; i <= count; i++) {
      const t = i / (count + 1);
      const x = x0 + (x1 - x0) * t;
      const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * (y0 + sag * 2) + t * t * y0;
      s += lantern(x, y + r * 2.2, r);
    }
    return s;
  };

  const defs =
    `<defs>${vgrad(`${p}wl`, wall, wallLow, wallLow)}` +
    vgrad(`${p}fl`, wood, shade(wood, 0.2), shade(wood, 0.35)) +
    fade(`${p}lh`, P.orange, P.dark ? 0.35 : 0.22) +
    ball(`${p}ln`, P.dark ? shade(P.red, 0.1) : tint(P.red, 0.15), 0.45, 0.35) +
    body(
      `${p}gr`,
      P.dark ? shade(mix(P.gold, P.orange, 0.3), 0.15) : mix(P.gold, '#FFD166', 0.4),
      0.4,
      0.35,
      0.5,
      1,
    ) +
    body(`${p}jd`, mix(P.green, P.teal, 0.3), 0.4, 0.35, 0.5, 1) +
    ball(`${p}pc`, mix(P.pink, P.orange, 0.3), 0.45, 0.3) +
    fade(`${p}calm`, P.dark ? mix(P.bg, P.orange, 0.2) : '#FFFFFF', P.dark ? 0.35 : 0.55) +
    `</defs>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}wl)"/>` +
    `<rect y="${floor}" width="${W}" height="${H - floor}" fill="url(#${p}fl)" fill-opacity="${o(0.9, 0.45)}"/>` +
    `<path d="M0 ${floor}H${W}" stroke="${gold}" stroke-opacity="${o(0.35, 0.5)}" stroke-width="3"/>` +
    // the calm centre: a soft pool of warm light behind the machine
    `<ellipse cx="${W / 2}" cy="${H * 0.5}" rx="${n(W * 0.36)}" ry="${n(H * 0.44)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      // left wall: two shelves
      shelf(40, 400, 330) +
      shelf(40, 400, 560) +
      gourd(110, 330, 1.2) +
      jar(220, 330, 1.1) +
      peaches(330, 330, 1.1) +
      jar(90, 560, 0.9) +
      gourd(190, 560, 0.95) +
      peaches(310, 560, 1) +
      // right wall: two shelves
      shelf(1200, 1560, 360) +
      shelf(1200, 1560, 590) +
      peaches(1270, 360, 1.1) +
      gourd(1390, 360, 1.25) +
      jar(1490, 360, 1.05) +
      jar(1260, 590, 0.95) +
      gourd(1370, 590, 1) +
      peaches(1480, 590, 1) +
      garland(60, 440, 70, 22, 3, 12) +
      garland(1160, 1540, 80, 22, 3, 12) +
      gourd(120, 860, 1.4) +
      jar(1480, 860, 1.3);
  } else {
    art +=
      shelf(-10, 110, 240) +
      gourd(40, 240, 0.9) +
      shelf(290, 410, 280) +
      jar(350, 280, 0.9) +
      garland(-10, 150, 40, 14, 3, 8) +
      peaches(60, 780, 0.9) +
      gourd(350, 790, 1);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Lucky Lever: a gold cord with a tiny red palace lantern (repeat-x tile, 64 × 12). */
export function luckyLeverAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.4);
  const red = P.dark ? P.red : tint(P.red, 0.05);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 12" width="64" height="12">` +
    `<path d="M0 1.5Q32 7 64 1.5" fill="none" stroke="${gold}" stroke-opacity=".6"/>` +
    `<rect x="29" y="4.6" width="6" height="6" rx="1.6" fill="${red}"/>` +
    `<rect x="30.2" y="3.6" width="3.6" height="1.2" fill="${gold}"/><rect x="30.2" y="10.4" width="3.6" height="1.2" fill="${gold}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'lucky-lever', scene: luckyLeverScene, accent: luckyLeverAccent });
